/** Doctor-only wire mirror. Kept inside the frontend build boundary. */
export interface ClinicalExaminationView {
  readonly version: 1;
  readonly noNewProblemFound: boolean;
  readonly findings: readonly { readonly toothCode: string; readonly label: string; readonly note: string; readonly noTreatmentNeeded?: boolean }[];
}
export const EMPTY_EXAMINATION: ClinicalExaminationView = { version: 1, noNewProblemFound: false, findings: [] };
export function examinationEqual(a: ClinicalExaminationView | null | undefined, b: ClinicalExaminationView | null | undefined): boolean {
  if (a == null || b == null) return a == null && b == null;
  return a.version === b.version && a.noNewProblemFound === b.noNewProblemFound &&
    a.findings.length === b.findings.length && a.findings.every((finding, index) => {
      const other = b.findings[index];
      return finding.toothCode === other.toothCode && finding.label === other.label && finding.note === other.note && finding.noTreatmentNeeded === other.noTreatmentNeeded;
    });
}
/** A mixed selection toggles to fully on; it never removes a finding from the
 * other selected tooth. Only current-visit facts are passed here. */
export function toggleFinding(value: ClinicalExaminationView | null | undefined, teeth: readonly string[], label: string): ClinicalExaminationView {
  const doc = value ?? EMPTY_EXAMINATION;
  const all = teeth.every(code => doc.findings.some(f => f.toothCode === code && f.label === label));
  const findings = all ? doc.findings.filter(f => !(teeth.includes(f.toothCode) && f.label === label)) : [...doc.findings,
    ...teeth.filter(code => !doc.findings.some(f => f.toothCode === code && f.label === label)).map(toothCode => ({ toothCode, label, note: '' }))];
  return { ...doc, noNewProblemFound: false, findings };
}
export const EXAMINATION_AREAS = [
  ['mouth', 'Full mouth'], ['upper', 'Upper arch'], ['lower', 'Lower arch'],
  ['q1', 'Upper right'], ['q2', 'Upper left'], ['q4', 'Lower right'], ['q3', 'Lower left'],
] as const;
export type ExaminationArea = typeof EXAMINATION_AREAS[number][0];

/** Saved/live view helper; private notes enter internal output only. Null or
 * an untouched empty document never asserts a healthy examination. */
export function examinationSummary(value: ClinicalExaminationView | null | undefined, includePrivateNotes = false, planItems?: readonly FindingPlanCoverage[]): string | null {
  if (!value) return null;
  if (value.noNewProblemFound) return 'No problem found';
  if (!value.findings.length) return null;
  return value.findings.map(f => `Tooth ${f.toothCode}: ${f.label}${findingTreatmentState(f, planItems) === 'not_needed' ? ' — No treatment needed' : ''}${includePrivateNotes && f.note ? ` — ${f.note}` : ''}`).join('\n');
}


export type ExaminationFinding = ClinicalExaminationView['findings'][number];
/** The existing saved canonical tooth references. Unstructured area text is
 * intentionally not a diagnosis/treatment link. */
export interface FindingPlanCoverage {
  readonly toothRefs?: readonly { readonly toothCode: string }[];
}
export type FindingTreatmentState = 'planned' | 'not_needed' | 'not_chosen' | 'unavailable';
const findingKey = (f: ExaminationFinding) => `${f.toothCode}:${f.label.trim().toLocaleLowerCase('en')}`;
/** Presentation coverage follows the prototype's tooth match, including a
 * recorded declined/advised line. This is not consent or completion eligibility.
 * Unknown reads and unlocated legacy/area lines never become a guessed match. */
export function findingTreatmentState(finding: ExaminationFinding, planItems: readonly FindingPlanCoverage[] | null | undefined): FindingTreatmentState {
  if (planItems?.some(row => row.toothRefs?.some(ref => ref.toothCode === finding.toothCode))) return 'planned';
  if (finding.noTreatmentNeeded === true) return 'not_needed';
  if (planItems == null || planItems.some(row => !row.toothRefs?.length)) return 'unavailable';
  return 'not_chosen';
}
export function findingTreatmentLabel(state: FindingTreatmentState): string {
  return { planned: 'Treatment recorded for this tooth', not_needed: 'No treatment needed',
    not_chosen: 'No treatment chosen', unavailable: 'Treatment link unavailable' }[state];
}
/** One derivation for the Plan action, Finish warnings and saved print. Passing
 * recorded findings protects original finalized content; additions remain new
 * facts. A missing plan never creates an affirmative no-treatment decision. */
export function examinationTreatmentReview(value: ClinicalExaminationView | null | undefined, planItems: readonly FindingPlanCoverage[] | null | undefined, recorded?: ClinicalExaminationView | null) {
  const originals = new Set(recorded?.findings.map(findingKey) ?? []);
  return (value?.findings ?? []).map(finding => {
    const state = findingTreatmentState(finding, planItems);
    const isRecorded = originals.has(findingKey(finding));
    return { finding, state, label: findingTreatmentLabel(state),
      canRecordNoTreatmentNeeded: !isRecorded && state === 'not_chosen',
      warning: state === 'planned' || state === 'not_needed' ? null :
        `${findingTreatmentLabel(state)} for ${finding.toothCode} ${finding.label}` };
  });
}
/** Explicit one-way choice, preserving every other finding and its note.
 * The existing stage save owns persistence and failure recovery. */
export function recordFindingNoTreatmentNeeded(value: ClinicalExaminationView, target: Pick<ExaminationFinding, 'toothCode' | 'label'>, recorded?: ClinicalExaminationView | null): ClinicalExaminationView {
  const key = findingKey({ ...target, note: '' });
  if (recorded?.findings.some(f => findingKey(f) === key)) return value;
  return { ...value, findings: value.findings.map(f => findingKey(f) === key ? { ...f, noTreatmentNeeded: true } : f) };
}
